import { FC } from 'react';
import { Grid } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title } from 'src/generic/component';
import { Checkboxes, Input, BooleanRadio } from 'src/generic/component/form';
import { Draft } from 'src/types/common';
import { IAIRequest, IOnsiteStudents, IEnrolledStudents,IAIStaff } from 'src/types/org/ai/schoolInterface';
import { IFieldErrors } from 'src/types/validation';
import { useBlurValidation } from 'src/hooks';
import { KEY_OTHER, TYPE_OPTIONS, CREDIT_OPTIONS, ACADEMIC_DISCIPLINES_OPTIONS, TEACHING_METHOD_OPTIONS } from '../aiFormConstants';
import 'src/styles/studentCount.css';

interface IOrganizationDetailsPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const OrganizationDetailsPanel: FC<IOrganizationDetailsPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  // Blur validation hook with combined error helpers
  const { handleBlur, handleInputBlur, getError, hasError, hasBlurError, getBlurError, clearBlurError, getDisplayError, hasDisplayError } = useBlurValidation(errors);

  const appliedLevel = item.data?.ai?.appliedLevel;
  const appForm = item.data?.appForm;
  const {
    type,
    typeOther,
    creditType,
    creditTypeOther,
    academicDisciplines,
    academicDisciplinesOther,
    teachingMethod,
    teachingMethodOther,
    onsiteStudents,
    studentsEnrolled,
    aiStaff
  } = appForm || {};

  const {
  totalStaff,
  hasRelativeEmployees,
  relativeEmployeeCount
} = aiStaff || {};

  const changed = (updates: Partial<typeof appForm>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: { ...appForm, ...updates }
      }
    };
    onUpdate(updatedItem);
  };

  // Handle student count changes
  const handleStudentCountChange = (grade: keyof IOnsiteStudents, value: string) => {
    // Only allow numeric input and limit to 4 digits
    let numericValue = value.replace(/[^0-9]/g, '').slice(0, 4);
    clearBlurError(`onsite_${grade}`);

    changed({
      onsiteStudents: {
        ...onsiteStudents,
        [grade]: numericValue
      }
    });
  };

  const handleEnrolledStudentCountChange = (grade: keyof IEnrolledStudents, value: string) => {
    let numericValue = value.replace(/[^0-9]/g, '').slice(0, 4);
    // Clear any on-site blur error for the corresponding grade since enrolled value is changing
    const matchingOnsite = gradeLabels.find(g => g.enrolledKey === grade);
    if (matchingOnsite) clearBlurError(`onsite_${matchingOnsite.key}`);

    changed({
      studentsEnrolled: {
        ...studentsEnrolled,
        [grade]: numericValue
      }
    });
  };

  const changedStaff = (updates: Partial<IAIStaff>) => {
  const updatedItem: Draft<IAIRequest> = {
    ...item,
    data: {
      ...item.data,
      appForm: {
        ...appForm,
        aiStaff: {
          ...appForm?.aiStaff,
          ...updates
        }
      }
    }
  };
  onUpdate(updatedItem);
};

const handleNumericInput = (value: string) => {
  return value.replace(/[^0-9]/g, '');
};

  // Grade labels array (same for both onsite and enrolled)
  const gradeLabels = [
    { key: 'zeroTaught', label: 'K:', enrolledKey: 'zeroEnrolled' },
    { key: 'oneTaught', label: '1st:', enrolledKey: 'oneEnrolled' },
    { key: 'twoTaught', label: '2nd:', enrolledKey: 'twoEnrolled' },
    { key: 'threeTaught', label: '3rd:', enrolledKey: 'threeEnrolled' },
    { key: 'fourTaught', label: '4th:', enrolledKey: 'fourEnrolled' },
    { key: 'fiveTaught', label: '5th:', enrolledKey: 'fiveEnrolled' },
    { key: 'sixTaught', label: '6th:', enrolledKey: 'sixEnrolled' },
    { key: 'sevenTaught', label: '7th:', enrolledKey: 'sevenEnrolled' },
    { key: 'eightTaught', label: '8th:', enrolledKey: 'eightEnrolled' },
    { key: 'nineTaught', label: '9th:', enrolledKey: 'nineEnrolled' },
    { key: 'tenTaught', label: '10th:', enrolledKey: 'tenEnrolled' },
    { key: 'elevenTaught', label: '11th:', enrolledKey: 'elevenEnrolled' },
    { key: 'twelveTaught', label: '12th:', enrolledKey: 'twelveEnrolled' }
  ];

  return (
    <Panel>
      <Title>Organization Details</Title>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          {/* Existing checkbox sections */}
          <FieldRow>
            <FieldLabel variant="body1">Type of School (check all that apply):</FieldLabel>
            <div className="organization-type-checkboxes" style={{ paddingTop: '5px' }}>
              <Checkboxes
                name='type'
                readOnly={readOnly}
                value={type || []}
                error={getError('data.appForm.type')}
                options={TYPE_OPTIONS.map(option => ({ key: option, label: option, style: { marginLeft: '8px' } }))}
                onChange={(values: string[]) => {
                  changed({
                    type: values,
                    typeOther: !values.includes(KEY_OTHER) ? undefined : typeOther
                  });
                }}
              />
            </div>
          </FieldRow>
          {type?.includes(KEY_OTHER) &&
            <FieldRow>
              <FieldLabel variant="body1">Please provide an explanation of your school type</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasDisplayError('typeOther', 'data.appForm.typeOther') ? 'error' : undefined}
                validationMsg={getDisplayError('typeOther', 'data.appForm.typeOther')}
                onChange={(e) => {
                  changed({ typeOther: e.target.value });
                  clearBlurError('typeOther');
                }}
                onBlur={() => handleInputBlur('typeOther', typeOther || '', true)}
                value={typeOther || ''}
                maxLength={255}
              />
            </FieldRow>
          }

          <FieldRow style={{ marginTop: '18px' }}>
            <FieldLabel variant="body1">Please check the statement(s) that best describe your organization (check all that apply):</FieldLabel>
            <div className="organization-type-checkboxes" style={{ paddingTop: '5px' }}>
              <Checkboxes
                name='creditType'
                readOnly={readOnly}
                value={creditType || []}
                error={getError('data.appForm.creditType')}
                options={CREDIT_OPTIONS.map(option => ({ key: option, label: option }))}
                onChange={(values: string[]) => {
                  changed({
                    creditType: values,
                    creditTypeOther: !values.includes(KEY_OTHER) ? undefined : creditTypeOther
                  });
                }}
              />
            </div>
          </FieldRow>
          {creditType?.includes(KEY_OTHER) &&
            <FieldRow>
              <FieldLabel variant="body1">Please explain</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasDisplayError('creditTypeOther', 'data.appForm.creditTypeOther') ? 'error' : undefined}
                validationMsg={getDisplayError('creditTypeOther', 'data.appForm.creditTypeOther')}
                onChange={(e) => {
                  changed({ creditTypeOther: e.target.value });
                  clearBlurError('creditTypeOther');
                }}
                onBlur={() => handleInputBlur('creditTypeOther', creditTypeOther || '', true)}
                value={creditTypeOther || ''}
                maxLength={255}
              />
            </FieldRow>
          }

          <FieldRow style={{ marginTop: '18px' }}>
            <FieldLabel variant="body1">Academic disciplines offered:</FieldLabel>
            <div className="organization-type-checkboxes" style={{ paddingTop: '5px' }}>
              <Checkboxes
                name='academicDisciplines'
                readOnly={readOnly}
                value={academicDisciplines || []}
                error={getError('data.appForm.academicDisciplines')}
                options={ACADEMIC_DISCIPLINES_OPTIONS.map(option => ({ key: option, label: option }))}
                onChange={(values: string[]) => {
                  changed({
                    academicDisciplines: values,
                    academicDisciplinesOther: !values.includes(KEY_OTHER) ? undefined : academicDisciplinesOther
                  });
                }}
              />
            </div>
          </FieldRow>
          {academicDisciplines?.includes(KEY_OTHER) &&
            <FieldRow>
              <FieldLabel variant="body1">Please list</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasDisplayError('academicDisciplinesOther', 'data.appForm.academicDisciplinesOther') ? 'error' : undefined}
                validationMsg={getDisplayError('academicDisciplinesOther', 'data.appForm.academicDisciplinesOther')}
                onChange={(e) => {
                  changed({ academicDisciplinesOther: e.target.value });
                  clearBlurError('academicDisciplinesOther');
                }}
                onBlur={() => handleInputBlur('academicDisciplinesOther', academicDisciplinesOther || '', true)}
                value={academicDisciplinesOther || ''}
                maxLength={255}
              />
            </FieldRow>
          }

          <FieldRow style={{ marginTop: '18px' }}>
            <FieldLabel variant="body1">School primarily teaches:</FieldLabel>
            <div className="organization-type-checkboxes" style={{ paddingTop: '5px' }}>
              <Checkboxes
                name='teachingMethod'
                readOnly={readOnly}
                value={teachingMethod || []}
                error={getError('data.appForm.teachingMethod')}
                options={TEACHING_METHOD_OPTIONS.map(option => ({ key: option, label: option }))}
                onChange={(values: string[]) => {
                  changed({
                    teachingMethod: values,
                    teachingMethodOther: !values.includes(KEY_OTHER) ? undefined : teachingMethodOther
                  });
                }}
              />
            </div>
          </FieldRow>
          {teachingMethod?.includes(KEY_OTHER) &&
            <FieldRow>
              <FieldLabel variant="body1">Please explain</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasDisplayError('teachingMethodOther', 'data.appForm.teachingMethodOther') ? 'error' : undefined}
                validationMsg={getDisplayError('teachingMethodOther', 'data.appForm.teachingMethodOther')}
                onChange={(e) => {
                  changed({ teachingMethodOther: e.target.value });
                  clearBlurError('teachingMethodOther');
                }}
                onBlur={() => handleInputBlur('teachingMethodOther', teachingMethodOther || '', true)}
                value={teachingMethodOther || ''}
                maxLength={255}
              />
            </FieldRow>
          }
          
          {/* STAFF QUESTIONS SECTION - MOVED FROM TestPrepPanel */}
          {/* Only show staff questions for Level 2 Authorization */}
          {appliedLevel === '2' && (
          <>
            <FieldRow style={{ marginTop: '18px' }}>
              <FieldLabel variant="body1">
                Please enter the total number of teachers and administrators employed at your organization
              </FieldLabel>
              <Input
                disabled={readOnly}
                value={totalStaff || ''}
                validation={hasError('data.appForm.aiStaff.totalStaff') ? 'error' : undefined}
                validationMsg={getError('data.appForm.aiStaff.totalStaff')}
                onChange={(e) => {
                  const sanitizedValue = handleNumericInput(e.target.value);
                  changedStaff({ totalStaff: sanitizedValue });
                }}
                style={{ width: '100px' }}
              />
            </FieldRow>

            {/* 2. Relatives employed? */}
            <FieldRow>
              <FieldLabel variant="body1">
                Are there any current employees of the school who are relatives or household members of students who attend the school?
              </FieldLabel>
              <BooleanRadio
                readOnly={readOnly}
                error={getError('data.appForm.aiStaff.hasRelativeEmployees')}
                value={hasRelativeEmployees}
                name='hasRelativeEmployees'
                onChange={(value) => {
                  const updates: Partial<IAIStaff> = { hasRelativeEmployees: value };
                  if (!value) {
                    updates.relativeEmployeeCount = undefined;
                  }
                  changedStaff(updates);
                }}
              />
            </FieldRow>

            {/* 3. Conditional: How many relatives? */}
            {hasRelativeEmployees && (
              <FieldRow>
                <FieldLabel variant="body1">
                  How many teachers and administrators are relatives or household members of enrolled students?
                </FieldLabel>
                <Input
                  disabled={readOnly}
                  value={relativeEmployeeCount || ''}
                  validation={hasError('data.appForm.aiStaff.relativeEmployeeCount') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.aiStaff.relativeEmployeeCount')}
                  onChange={(e) => {
                    const sanitizedValue = handleNumericInput(e.target.value);
                    changedStaff({ relativeEmployeeCount: sanitizedValue });
                  }}
                  style={{ width: '100px' }}
                />
              </FieldRow>
            )}
          </>
          )}
          {/* NEW SECTION: Number of students  enrolled */}
          <FieldRow style={{ marginTop: '18px' }}>
            <FieldLabel variant="body1">Please enter number of students enrolled in each grade(Please be sure to only include students enrolled at this specific campus location):</FieldLabel>
            <div className="student-count-grid">
              {gradeLabels.map(({ key, enrolledKey, label }) => (
                <div key={enrolledKey} className="student-count-item">
                  <span className="student-count-label">{label} *</span>
                  <input
                    className={`student-count-input ${hasError(`data.appForm.studentsEnrolled.${enrolledKey}`) ? 'error' : ''}`}
                    disabled={readOnly}
                    onChange={(e) => handleEnrolledStudentCountChange(enrolledKey as keyof IEnrolledStudents, e.target.value)}
                    onBlur={() => {
                      const onsiteVal = parseInt(onsiteStudents?.[key as keyof IOnsiteStudents] || '');
                      const enrolledVal = parseInt(studentsEnrolled?.[enrolledKey as keyof IEnrolledStudents] || '');
                      const gradeLabel = label.replace(':', '');
                      if (!isNaN(onsiteVal) && !isNaN(enrolledVal) && onsiteVal > enrolledVal) {
                        handleBlur(`onsite_${key}`, String(onsiteVal), () => `Grade ${gradeLabel} taught on-site (${onsiteVal}) cannot exceed total students enrolled (${enrolledVal}) in grade ${gradeLabel}`);
                      } else {
                        clearBlurError(`onsite_${key}`);
                      }
                    }}
                    value={studentsEnrolled?.[enrolledKey as keyof IEnrolledStudents] || ''}
                    maxLength={4}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    type="text"
                    min="0"
                    max="9999"
                  />
                  {hasError(`data.appForm.studentsEnrolled.${enrolledKey}`) && (
                    <div className="error-message">
                      {getError(`data.appForm.studentsEnrolled.${enrolledKey}`)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </FieldRow>
          
          {/* NEW SECTION: Number of students on-site */}
          <FieldRow style={{ marginTop: '18px' }}>
            <FieldLabel variant="body1">Please enter number of students taught on-site in each grade:</FieldLabel>
            <div className="student-count-grid">
              {gradeLabels.map(({ key, label, enrolledKey }) => (
                <div key={key} className="student-count-item">
                  <span className="student-count-label">{label} *</span>
                  <input
                    className={`student-count-input ${(hasError(`data.appForm.onsiteStudents.${key}`) || hasBlurError(`onsite_${key}`)) ? 'error' : ''}`}
                    disabled={readOnly}
                    onChange={(e) => handleStudentCountChange(key as keyof IOnsiteStudents, e.target.value)}
                    onBlur={() => {
                      const onsiteVal = parseInt(onsiteStudents?.[key as keyof IOnsiteStudents] || '');
                      const enrolledVal = parseInt(studentsEnrolled?.[enrolledKey as keyof IEnrolledStudents] || '');
                      const gradeLabel = label.replace(':', '');
                      if (!isNaN(onsiteVal) && !isNaN(enrolledVal) && onsiteVal > enrolledVal) {
                        handleBlur(`onsite_${key}`, String(onsiteVal), () => `Grade ${gradeLabel} taught on-site (${onsiteVal}) cannot exceed total students enrolled (${enrolledVal}) in grade ${gradeLabel}`);
                      } else {
                        clearBlurError(`onsite_${key}`);
                      }
                    }}
                    value={onsiteStudents?.[key as keyof IOnsiteStudents] || ''}
                    maxLength={4}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    type="text"
                    min="0"
                    max="9999"
                  />
                  {(hasError(`data.appForm.onsiteStudents.${key}`) || hasBlurError(`onsite_${key}`)) && (
                    <div className="error-message">
                      {getError(`data.appForm.onsiteStudents.${key}`) || getBlurError(`onsite_${key}`)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </FieldRow>
        </Grid>
      </Grid>
    </Panel>
  );
};